import { useMutation } from '@apollo/client';
// Api
import {
  CREATE_CHANNEL_MONETIZATION_PROGRAM,
  UPDATE_CHANNEL_MONETIZATION_PROGRAM,
  SET_CHANNEL_MONETIZATION_MILESTONE_TARGETS,
} from 'api/channelMonetization/mutations';
import { LIST_CHANNEL_MONETIZATION_PROGRAMS } from 'api/channelMonetization/queries';
// Types
import {
  CreateChannelMonetizationProgram,
  CreateChannelMonetizationProgramVariables,
} from 'api/channelMonetization/types/CreateChannelMonetizationProgram';
import {
  UpdateChannelMonetizationProgram,
  UpdateChannelMonetizationProgramVariables,
} from 'api/channelMonetization/types/UpdateChannelMonetizationProgram';
import {
  SetChannelMonetizationMilestoneTargets,
  SetChannelMonetizationMilestoneTargetsVariables,
} from 'api/channelMonetization/types/SetChannelMonetizationMilestoneTargets';
import { ListChannelMonetizationPrograms_listChannelMonetizationPrograms } from 'api/channelMonetization/types/ListChannelMonetizationPrograms';
import { MilestoneRow, ProgramFormValues } from './types';
// Helpers
import {
  buildSetTargetsInput,
  isValidMonthLabel,
  normalizeProgramFormValues,
} from './helpers';
// UI
import { errorNotification, successNotification } from 'ui/Notification';

type SaveParams = {
  values: ProgramFormValues;
  milestones: MilestoneRow[];
  isEdit: boolean;
  program: ListChannelMonetizationPrograms_listChannelMonetizationPrograms | null;
};

type UseSaveProgramResult = {
  save: (params: SaveParams) => Promise<boolean>;
  saving: boolean;
};

export const useSaveProgram = (): UseSaveProgramResult => {
  const [createProgram, { loading: createLoading }] = useMutation<
    CreateChannelMonetizationProgram,
    CreateChannelMonetizationProgramVariables
  >(CREATE_CHANNEL_MONETIZATION_PROGRAM, {
    refetchQueries: [{ query: LIST_CHANNEL_MONETIZATION_PROGRAMS }],
  });
  const [updateProgram, { loading: updateLoading }] = useMutation<
    UpdateChannelMonetizationProgram,
    UpdateChannelMonetizationProgramVariables
  >(UPDATE_CHANNEL_MONETIZATION_PROGRAM);
  const [setTargets, { loading: targetsLoading }] = useMutation<
    SetChannelMonetizationMilestoneTargets,
    SetChannelMonetizationMilestoneTargetsVariables
  >(SET_CHANNEL_MONETIZATION_MILESTONE_TARGETS);

  const saving = createLoading || updateLoading || targetsLoading;

  const save = async ({
    values,
    milestones,
    isEdit,
    program,
  }: SaveParams): Promise<boolean> => {
    try {
      const normalized = normalizeProgramFormValues(values);

      if (!isValidMonthLabel(normalized.monthLabel)) {
        errorNotification('Month must be in YYYY-MM format');
        return false;
      }

      let programId: string;

      if (isEdit && program) {
        const updated = await updateProgram({
          variables: {
            input: {
              programId: program.id,
              prizeAmount: normalized.prizeAmount,
              prizeCurrency: normalized.prizeCurrency,
              slotsPerMonth: normalized.slotsPerMonth,
            },
          },
        });
        programId =
          updated.data?.updateChannelMonetizationProgram.id ?? program.id;
      } else {
        const created = await createProgram({
          variables: {
            input: {
              monthLabel: normalized.monthLabel,
              prizeAmount: normalized.prizeAmount,
              prizeCurrency: normalized.prizeCurrency,
              slotsPerMonth: normalized.slotsPerMonth,
            },
          },
        });
        if (!created.data?.createChannelMonetizationProgram.id) {
          throw new Error('Program creation returned no id');
        }
        programId = created.data.createChannelMonetizationProgram.id;
      }

      await setTargets({
        variables: { input: buildSetTargetsInput(programId, milestones) },
      });

      successNotification(isEdit ? 'Program updated' : 'Program created');
      return true;
    } catch (error) {
      errorNotification((error as Error).message);
      return false;
    }
  };

  return { save, saving };
};
